import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Box } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import { MoveTimeCriteriaType } from '~/types';
import CriteriaFacet from './CriteriaFacet';
import DateRangeField from './DateRangeField';

const useStyles = makeStyles(theme => ({
  criteriaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  criteriaField: {
    width: '100%'
  }
}));

const criteriaLabelMap = {
  [MoveTimeCriteriaType.Department]: 'moveTimeDialog.criteria.department',
  [MoveTimeCriteriaType.Location]: 'moveTimeDialog.criteria.location',
  [MoveTimeCriteriaType.EmployeeType]: 'moveTimeDialog.criteria.employeeType',
  [MoveTimeCriteriaType.CostCenter]: 'moveTimeDialog.criteria.costCenter',
  [MoveTimeCriteriaType.ServiceCenter]: 'moveTimeDialog.criteria.serviceCenter',
  [MoveTimeCriteriaType.Division]: 'moveTimeDialog.criteria.division',
  [MoveTimeCriteriaType.User]: 'moveTimeDialog.criteria.user'
};

const criteriaDataQeIdMap = {
  [MoveTimeCriteriaType.Department]: 'MoveTimeDialogDepartmentCriteria',
  [MoveTimeCriteriaType.Location]: 'MoveTimeDialogLocationCriteria',
  [MoveTimeCriteriaType.EmployeeType]: 'MoveTimeDialogEmployeeTypeCriteria',
  [MoveTimeCriteriaType.CostCenter]: 'MoveTimeDialogCostCenterCriteria',
  [MoveTimeCriteriaType.ServiceCenter]: 'MoveTimeDialogServiceCenterCriteria',
  [MoveTimeCriteriaType.Division]: 'MoveTimeDialogDivisionCriteria',
  [MoveTimeCriteriaType.User]: 'MoveTimeDialogUserCriteria'
};

const CriteriaItem = ({
  criterion,
  onRemove,
  onChange,
  projectId,
  disabled
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const handleCriterionChange = useCallback(
    value => {
      onChange({ ...criterion, value });
    },
    [criterion, onChange]
  );

  const labelKey = criteriaLabelMap[criterion.type];
  const dataQeId = criteriaDataQeIdMap[criterion.type];

  const criteriaField = useMemo(() => {
    if (criterion.type === MoveTimeCriteriaType.DateRange) {
      return (
        <DateRangeField
          value={criterion.value || null}
          onChange={handleCriterionChange}
          disabled={disabled}
        />
      );
    }

    if (!labelKey) return null;

    return (
      <CriteriaFacet
        criterionType={criterion.type}
        value={criterion.value}
        onChange={handleCriterionChange}
        label={intl.formatMessage({ id: labelKey })}
        disabled={disabled}
        projectId={projectId}
        data-qe-id={dataQeId}
        aria-label={intl.formatMessage({ id: labelKey })}
      />
    );
  }, [
    criterion.type,
    criterion.value,
    labelKey,
    dataQeId,
    intl,
    disabled,
    handleCriterionChange,
    projectId
  ]);

  return (
    <Box
      className={classes.criteriaRow}
      data-qe-id={`MoveTimeDialogCriteriaItem-${criterion.type}`}
    >
      <div className={classes.criteriaField}>{criteriaField}</div>
      <IconButton
        onClick={onRemove}
        size="small"
        aria-label={intl.formatMessage({ id: 'moveTimeDialog.removeCriteria' })}
        disabled={disabled}
        data-qe-id={`MoveTimeDialogRemoveCriteria-${criterion.type}`}
      >
        <RemoveCircleOutlineIcon />
      </IconButton>
    </Box>
  );
};

CriteriaItem.propTypes = {
  criterion: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([
      MoveTimeCriteriaType.Department,
      MoveTimeCriteriaType.Location,
      MoveTimeCriteriaType.EmployeeType,
      MoveTimeCriteriaType.CostCenter,
      MoveTimeCriteriaType.ServiceCenter,
      MoveTimeCriteriaType.Division,
      MoveTimeCriteriaType.DateRange,
      MoveTimeCriteriaType.User
    ]).isRequired,
    value: PropTypes.any
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  disabled: PropTypes.bool
};

export default CriteriaItem;
